'use client'

import type React from 'react'
import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import config from '@/config/config.env'
import { getAuthToken } from '@/lib/auth-token'
import { nanoid } from 'nanoid'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-hot-toast'
import { getWorkerFile } from '@/utils/helpers/workers'

// Type definitions
interface Document {
  id: string
  name: string
  url?: string
  // Add other document properties as needed
}

interface JobData {
  jobId: string
  jobName: string
}

interface QueueData {
  isQueued: boolean
  queuedMessage: string
}

interface Modal {
  open: boolean
  minimize: boolean
}

interface SelectedFile {
  file: File
  id: string
}
type UploadResponse = {
  status: 'successs' // Note: 'successs' has 3 's'. If it's a typo, correct it to "success"
  message: string
  data: {
    operation_id: string
  }
}

interface WorkerMessage {
  isCompleted?: boolean
  error?: string
  documents?: Document[]
  isQueued?: boolean
  queuedMessage?: string
  progress?: number
  queueResponse?: UploadResponse
}

interface FileUploadContextType {
  isLoading: boolean
  data: Document[] | null
  operationData: any
  progress: number
  queueData: QueueData
  error: string | null
  sendPostMessage: (obj: any) => void
  handleOpen: (id: string, name: string) => void
  handleClose: () => void
  handleMinimize: () => void
  modal: Modal
  jobData: JobData
  selectedFiles: SelectedFile[]
  setProgress: (progress: number) => void
  setSelectedFiles: React.Dispatch<React.SetStateAction<SelectedFile[]>>
  uploadFiles: () => Promise<void>
  dropzone: {
    getRootProps: () => any
    getInputProps: () => any
    isDragActive: boolean
  }
}

interface WorkerProviderProps {
  children: ReactNode
}

const FileUploadContext = createContext<FileUploadContextType | undefined>(
  undefined
)

const WorkerProvider: React.FC<WorkerProviderProps> = ({ children }) => {
  const [worker, setWorker] = useState<Worker | null>(null)
  const queryClient = useQueryClient()
  const [data, setData] = useState<Document[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [operationData, setOperationData] = useState<UploadResponse | null>(
    null
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([])
  const [jobData, setJobData] = useState<JobData>({
    jobId: '',
    jobName: '',
  })
  const [queueData, setQueueData] = useState<QueueData>({
    isQueued: false,
    queuedMessage: '',
  })

  // OPEN AND MINIMIZE
  const [modal, setModal] = useState<Modal>({
    open: false,
    minimize: false,
  })

  // Dropzone configuration
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length <= 2000) {
      const documents: SelectedFile[] = acceptedFiles.map((file) => ({
        file,
        id: nanoid(6),
      }))
      setSelectedFiles(documents)
      // Don't auto-open modal here - let the parent component handle it
    } else {
      toast.error('Files should be less than or equal to 2000')
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        [],
    },
    multiple: true,
  })

  useEffect(() => {
    const workerInstance = new Worker(
      getWorkerFile('/app/webworkers/file-upload-handler.js')
    )
    setWorker(workerInstance)

    return () => {
      workerInstance.terminate()
    }
  }, [])

  useEffect(() => {
    if (worker) {
      worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
        if (e.data.isCompleted) {
          if ('error' in e.data && e.data.error) {
            setError(e.data.error)
            setIsLoading(false)
          } else {
            setProgress(100)
            setData(e.data.documents || null)
            setOperationData(e.data.queueResponse || null)

            if (e.data.isQueued) {
              setQueueData({
                isQueued: true,
                queuedMessage: e.data.queuedMessage || '',
              })
              if (modal.minimize) {
                toast.success("You'll be notified once it's done")
              }
            } else {
              setQueueData({
                isQueued: false,
                queuedMessage: '',
              })
              if (modal.minimize) {
                toast.success('Your documents have been successfully uploaded')
              }
            }
            setIsLoading(false)
          }
        } else if (e.data.progress) {
          setProgress(e.data.progress)
        }
      }

      worker.onerror = (e: ErrorEvent) => {
        setIsLoading(false)
        setError(e.message)
        handleClose()
        setProgress(0)
        setData(null)
        setOperationData(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker, modal.minimize])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isLoading) {
        event.preventDefault()
        const message =
          'Files are uploading. If you leave now, the upload will be cancelled.'
        event.returnValue = message
        return message
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isLoading])

  // Function to post messages to the worker
  const postMessage = useCallback(
    (message: any) => {
      if (worker) {
        setIsLoading(true)
        worker.postMessage(message)
      }
    },
    [worker]
  )

  const handleOpen = (id: string, name: string) => {
    if (id) {
      setJobData({ jobId: id, jobName: name })
      setModal({ minimize: false, open: true })
    }
  }

  const handleClose = useCallback(() => {
    // Invalidate queries only if we have data and a job ID
    if (data && data.length > 0 && jobData.jobId) {
      queryClient.invalidateQueries({
        queryKey: ['job-applicants', jobData.jobId],
      })
    }

    // Reset all state
    setProgress(0)
    setData(null)
    setOperationData(null)
    setError(null)
    setSelectedFiles([])
    setJobData({ jobId: '', jobName: '' })
    setModal({ minimize: false, open: false })
    setQueueData({
      isQueued: false,
      queuedMessage: '',
    })
  }, [data, jobData.jobId, queryClient])

  const handleMinimize = useCallback(() => {
    setModal((prevData) => ({
      ...prevData,
      minimize: !prevData.minimize,
    }))
  }, [])

  const sendPostMessage = useCallback(
    (obj: any) => {
      postMessage(obj)
    },
    [postMessage]
  )

  const uploadFiles = useCallback(async () => {
    try {
      const authToken = await getAuthToken()
      if (!authToken) {
        throw new Error('Authentication required')
      }

      const documents = [...selectedFiles]
      if (documents.length === 0) {
        toast.error('Please select files to upload')
        return
      }

      sendPostMessage({
        URL: config.urls.baseUrl,
        documents,
        authToken,
        jobId: jobData.jobId,
      })

      // Clear selected files after sending to worker
      setSelectedFiles([])
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed'
      setError(errorMessage)
      toast.error(errorMessage)
    }
  }, [selectedFiles, sendPostMessage, jobData.jobId])

  const contextValue: FileUploadContextType = {
    isLoading,
    data,
    operationData,
    progress,
    queueData,
    error,
    sendPostMessage,
    handleOpen,
    handleClose,
    handleMinimize,
    modal,
    jobData,
    selectedFiles,
    setProgress,
    setSelectedFiles,
    uploadFiles,
    dropzone: { getRootProps, getInputProps, isDragActive },
  }

  return (
    <FileUploadContext.Provider value={contextValue}>
      {children}
    </FileUploadContext.Provider>
  )
}

export default WorkerProvider

export const useFileUploadContext = (): FileUploadContextType => {
  const context = useContext(FileUploadContext)
  if (context === undefined) {
    throw new Error('useFileUploadContext must be used within a WorkerProvider')
  }
  return context
}
